import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Camera, ExternalLink, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, fileUrl } from '@/lib/api';
import { AAWTA } from '@/lib/aawta';

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

const CATEGORIES = [
  'All', 'Sponsors', 'Speakers', 'Exhibitors', 'Summit Highlights',
  'Partner Stories', 'Behind the Scenes', 'Promotional Media',
];

const Lightbox = ({ items, index, onClose, onNav }) => {
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNav(1);
      if (e.key === 'ArrowLeft') onNav(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onNav]);

  if (!item) return null;
  const src = fileUrl(item.media_file_id);

  return (
    <div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={item.title || 'Gallery image'}
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-5 py-4 text-white">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/60 font-semibold">
          {item.category || 'Gallery'} · {index + 1} of {items.length}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          data-testid="gallery-lightbox-close"
          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 md:px-16 relative" onClick={(e) => e.stopPropagation()}>
        {items.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); onNav(-1); }}
            data-testid="gallery-lightbox-prev"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Previous"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {item.media_file_id ? (
          <img
            src={src}
            alt={item.alt_text || item.title || ''}
            className="max-h-[74vh] max-w-full object-contain rounded-md shadow-[0_30px_100px_rgba(0,0,0,0.6)]"
            data-testid="gallery-lightbox-image"
          />
        ) : (
          <div className="text-white/70">No media</div>
        )}
        {items.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); onNav(1); }}
            data-testid="gallery-lightbox-next"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      {(item.title || item.caption) && (
        <div className="bg-black/60 border-t border-white/10" onClick={(e) => e.stopPropagation()}>
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-5 md:py-6">
            {item.title && (
              <div className="font-display font-semibold text-xl md:text-2xl text-white" data-testid="gallery-lightbox-title">
                {item.title}
              </div>
            )}
            {item.caption && <CaptionBlock caption={item.caption} />}
          </div>
        </div>
      )}
    </div>
  );
};

const CaptionBlock = ({ caption }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = caption.length > 260;
  const short = isLong ? caption.slice(0, 260).trimEnd() + '…' : caption;
  return (
    <div className="mt-2">
      <div
        data-testid="gallery-lightbox-caption"
        className={`text-[14.5px] md:text-[15px] leading-relaxed text-white/85 whitespace-pre-wrap ${
          expanded ? 'max-h-[35vh] overflow-y-auto pr-2' : ''
        }`}
      >
        {expanded ? caption : short}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="gallery-lightbox-readmore"
          className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-aawta-magenta hover:text-white transition-colors"
        >
          {expanded ? 'Show Less' : 'Read More'}
        </button>
      )}
    </div>
  );
};

export const Gallery = () => {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState('All');
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    api.get('/gallery').then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (cat === 'All') return items;
    return items.filter((it) => (it.category || '').toLowerCase() === cat.toLowerCase());
  }, [items, cat]);

  const has = items && items.length > 0;

  const nav = useCallback((dir) => {
    setOpenIdx((prev) => {
      if (prev === null) return null;
      const n = filtered.length;
      return ((prev + dir) % n + n) % n;
    });
  }, [filtered.length]);

  return (
    <section id="gallery" className="py-24 md:py-32 bg-aawta-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <span className="overline text-aawta-magenta">Gallery</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              The moments. The people. The movement.
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Photos, promotional media, and partner stories from the Future Workforce Summit.
              Tap any image to view in full size.
            </p>
          </div>
          <Button
            onClick={() => openExternal(AAWTA.social.instagram)}
            variant="outline"
            className="rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white font-medium px-6 py-6"
          >
            Follow on Instagram <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {has && (
          <div className="mt-10 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-[12.5px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors ${
                  cat === c ? 'bg-aawta-navy text-white' : 'bg-white text-aawta-navy border border-aawta-border hover:border-aawta-magenta'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {has ? filtered.map((g, i) => (
            <button
              key={g.id}
              onClick={() => setOpenIdx(i)}
              data-testid={`gallery-item-${g.id}`}
              className={`group relative rounded-2xl overflow-hidden bg-white border border-aawta-border block ${i % 5 === 0 ? 'aspect-[4/5]' : 'aspect-square'} focus:outline-none focus:ring-2 focus:ring-aawta-magenta focus:ring-offset-2 transition-transform hover:-translate-y-0.5 duration-200`}
              aria-label={g.title || g.alt_text || 'Open image'}
            >
              {g.media_file_id ? (
                <img src={fileUrl(g.media_file_id)} alt={g.alt_text || g.title || ''} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-aawta-navy/5 to-aawta-magenta/5">
                  <Camera className="h-8 w-8 text-aawta-navy/25" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 text-aawta-navy opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <ZoomIn className="h-4 w-4" />
              </div>
              {(g.title || g.caption) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-left">
                  <div className="text-white text-[12.5px] font-semibold truncate">{g.title}</div>
                  {g.caption && <div className="text-white/80 text-[11px] truncate">{g.caption}</div>}
                </div>
              )}
            </button>
          )) : Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={`relative rounded-2xl overflow-hidden bg-white border border-aawta-border ${i % 3 === 0 ? 'aspect-[4/5]' : 'aspect-square'}`}>
              <div className="absolute inset-0 bg-gradient-to-br from-aawta-navy/5 via-aawta-magenta/5 to-aawta-navy/10" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <Camera className="h-8 w-8 text-aawta-navy/25" />
                <div className="text-[10px] tracking-[0.22em] uppercase text-aawta-navy/40 font-semibold">Post-Event</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {openIdx !== null && filtered.length > 0 && (
        <Lightbox
          items={filtered}
          index={openIdx}
          onClose={() => setOpenIdx(null)}
          onNav={nav}
        />
      )}
    </section>
  );
};

export default Gallery;
